import { Box, Stack } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useLiteralListQueryParamState } from "hooks/useLiteralListQueryParamState";
import { useStringQueryParamState } from "hooks/useStringQueryParamState";
import { MultiSelect } from "../../common/inputs/multiSelect";
import { SelectBox } from "../Selectbox";
import { GroupedDeliverySeries } from "../types";
import { DeliveriesPlot } from "./DeliveriesPlot";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { ExcelDownloadButton } from "components/common/ExcelDownloadButton";
import { backendApi, downloadAsExcel } from "src/store/api/enhancedApi";
import { useIsAdmin, useTenant } from "hooks/settings";
import { useDateRange } from "contexts/DateRangeContext";

const DELIVERY_SERIES_TYPES = ["mtd", "expectedMtd", "purchases"] as const;

type DeliverySeriesType = (typeof DELIVERY_SERIES_TYPES)[number];

const SERIES_TYPE_TRANSLATION_KEYS: Record<DeliverySeriesType, string> = {
  mtd: "monthToDateDeliveries",
  expectedMtd: "expectedMonthToDateDeliveries",
  purchases: "monthPurchasedTotal",
};

interface ByMaterialDeliveriesProps {
  groupedDeliverySeries: GroupedDeliverySeries;
}

export const ByMaterialDeliveries = ({
  groupedDeliverySeries,
}: ByMaterialDeliveriesProps) => {
  const isAdmin = useIsAdmin();
  const { t } = useTenantTranslation();
  const tenant = useTenant();

  const [selectedMaterial, setSelectedMaterial] = useStringQueryParamState(
    "deliveriesMaterial",
    groupedDeliverySeries.availableMaterialNames[0] ?? null
  );

  const [selectedSeriesTypes, setSelectedSeriesTypes] =
    useLiteralListQueryParamState<DeliverySeriesType>({
      paramName: "seriesTypes",
      permitted: DELIVERY_SERIES_TYPES,
      localStorageKey: `${tenant}.strategyDashboard.deliveries.selectedSeriesTypes`,
      defaultValue: DELIVERY_SERIES_TYPES,
    });

  return (
    <Stack spacing={2} sx={{ width: "100%", height: "100%" }}>
      <Stack direction="row" spacing={2} justifyContent="space-between">
        <DateRangeSelector />
        {isAdmin ? <DeliveriesExcelButton /> : null}
      </Stack>
      <SelectBox
        availableOptions={groupedDeliverySeries.availableMaterialNames}
        selectedOption={selectedMaterial}
        onOptionChange={setSelectedMaterial}
        id="material-select"
        labelId="material-select-label"
        label={t("material")}
      />
      <MultiSelect<DeliverySeriesType, DeliverySeriesType>
        options={[...DELIVERY_SERIES_TYPES]}
        getOptionId={(seriesType) => seriesType}
        getOptionLabel={(seriesType) =>
          t(SERIES_TYPE_TRANSLATION_KEYS[seriesType])
        }
        values={selectedSeriesTypes}
        setValues={setSelectedSeriesTypes}
        small
        selectAll
        labelAbove
        label={t("seriesTypes")}
      />
      <Box sx={{ flexGrow: 1, minHeight: 500 }}>
        <DeliveriesPlot
          deliverySeries={groupedDeliverySeries}
          selectedMaterial={selectedMaterial}
          selectedSeriesTypes={selectedSeriesTypes}
        />
      </Box>
    </Stack>
  );
};

const DeliveriesExcelButton = () => {
  const { startDate, endDate } = useDateRange();
  const start = startDate.format();
  const end = endDate.format();
  const tenantName = useTenant();
  const [trigger] =
    backendApi.endpoints.getStrategyDashboardDeliveriesAndPurchases.useLazyQuery();
  const getBlob = () =>
    downloadAsExcel(trigger, {
      tenantName,
      start,
      end,
      "Cache-Control": "no-cache",
    });
  return <ExcelDownloadButton variant="outlined" getBlob={getBlob} />;
};
